import { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { db, COLLECTIONS, ClassRole, waitForAuthReady } from '../../../../utils/firebase';
import { collection, getDocs, query, onSnapshot, Timestamp } from 'firebase/firestore';

export interface Student {
  uid: string;
  name: string;
  number: number;
  points: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface PointApproval {
  id: string;
  uid: string;
  roleId: string;
  salaryType: 'point' | 'stamp';
  salaryPeriod: 'daily' | 'weekly' | 'monthly';
  targetDate: Date;
  isApproved: boolean;
  pointAutoPay: boolean;
  pointManagerApproved: boolean | null;
  teacherApproved: boolean | null;
  pointManagerApprovedAt: Date | null;
  teacherApprovedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

export const usePointApprovalData = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as any;
  const [students, setStudents] = useState<Student[]>([]);
  const [approvals, setApprovals] = useState<PointApproval[]>([]);
  const [classRoles, setClassRoles] = useState<Record<string, ClassRole>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teacher?.schoolId || !teacher?.classId) return;

    const fetchData = async () => {
      try {
    setLoading(true);
        await waitForAuthReady();

        // 학생 목록 가져오기
        const studentsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`);
        const studentsSnapshot = await getDocs(studentsRef);
        const studentsData = studentsSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as Student[];
        setStudents(studentsData);

        // 역할 정보 가져오기
        const rolesRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/classRoles`);
        const rolesSnapshot = await getDocs(rolesRef);
        const rolesData = rolesSnapshot.docs.reduce((acc, doc) => {
          const data = doc.data() as ClassRole;
          acc[data.id || doc.id] = {
            ...data,
            id: data.id || doc.id,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt
          };
          return acc;
        }, {} as Record<string, ClassRole>);
        console.log('역할 정보:', rolesData);
        setClassRoles(rolesData);

        // 승인 요청 리스너
        const approvalsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/pointApprovals`);
        const approvalsQuery = query(approvalsRef);
        
        const unsubscribe = onSnapshot(approvalsQuery, (snapshot) => {
          const approvalsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
            targetDate: doc.data().targetDate?.toDate(),
            teacherApprovedAt: doc.data().teacherApprovedAt?.toDate(),
            pointManagerApprovedAt: doc.data().pointManagerApprovedAt?.toDate()
          })) as PointApproval[];
          setApprovals(approvalsData);
        }, (error) => {
          console.error('승인 요청 로딩 중 오류:', error);
        setError('승인 요청을 불러오는 중 오류가 발생했습니다.');
        });

        return () => unsubscribe();
      } catch (err) {
        console.error('데이터 로딩 중 오류:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teacher?.schoolId, teacher?.classId]);

  const getStudentName = (uid: string) => {
    const student = students.find(s => s.uid === uid);
    return student ? `${student.number}번 ${student.name}` : '알 수 없음';
  };

  const getRoleName = (roleId: string) => classRoles[roleId]?.name || '알 수 없음';
  const getPointAmount = (roleId: string) => classRoles[roleId]?.salary || 0;
  const getPointAutoPay = (roleId: string) => {
    console.log('역할 ID:', roleId);
    console.log('역할 정보:', classRoles[roleId]);
    const role = classRoles[roleId];
    if (!role) {
      console.log('역할을 찾을 수 없음');
      return false;
    }
    return role.pointAutoPay === true;
  };

  const getPointStatus = (approval: PointApproval) => {
    console.log('승인 정보:', approval);
    console.log('자동 승인 여부:', getPointAutoPay(approval.roleId));
    
    if (approval.isApproved) {
      return { label: '승인됨', color: 'success' as const };
    }
    if (approval.teacherApproved === false || approval.pointManagerApproved === false) {
      return { label: '거절됨', color: 'error' as const };
      }
    if (approval.teacherApproved === null || approval.pointManagerApproved === null) {
      return { label: '승인 대기', color: 'warning' as const };
    }
    return null;
  };

  return {
    students,
    approvals,
    classRoles,
    loading,
    error,
    getStudentName,
    getRoleName,
    getPointAmount,
    getPointAutoPay,
    getPointStatus
  };
}; 